import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  HelpCircle,
  ChevronDown,
  Mail,
  ArrowRight,
  Workflow,
  Target,
  Compass,
  Bot,
  Zap,
  Building2,
  DollarSign,
  Clock,
  TrendingUp,
  ShieldAlert,
  Map as MapIcon,
  Crown,
  Users,
  Sparkles,
  Award,
  ArrowUpRight,
} from "lucide-react";
import { PageShell } from "./publicShared";
import { FAQ_ENTRIES } from "./faqEntries";

/* ═══════════════════════════════════════════════════
   FAQ MASTER PAGE — Executive Clarity
   • Full-width, responsive 2-col accordion
   • Each item links to its dedicated /faq/:slug page
   • Schema.org FAQPage JSON-LD for AEO
   ═══════════════════════════════════════════════════ */

const ICON_MAP = {
  Workflow, Target, Compass, Bot, Zap, Building2, DollarSign, Clock,
  TrendingUp, ShieldAlert, Map: MapIcon, Crown, Users, Sparkles, Award,
};

const StructuredData = () => {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ENTRIES.map((e) => ({
      "@type": "Question",
      "name": e.question,
      "acceptedAnswer": { "@type": "Answer", "text": e.answer },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

const FAQItem = ({ entry, idx, isOpen, onToggle }) => {
  const Icon = ICON_MAP[entry.icon] || HelpCircle;
  return (
    <div
      className={`rounded-2xl border bg-white transition-all ${
        isOpen
          ? "border-[#1a56db] shadow-[0_8px_32px_rgba(26,86,219,0.08)]"
          : "border-[#e5e7eb] hover:border-[#cbd5e1]"
      }`}
      data-testid={`faq-item-${idx}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-5 text-left px-6 py-5 sm:px-7 sm:py-6"
        aria-expanded={isOpen}
      >
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <span
            className={`mt-0.5 h-9 w-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
              isOpen ? "bg-[#1a56db] text-white" : "bg-[#e8eefb] text-[#1a56db]"
            }`}
          >
            <Icon className="h-4.5 w-4.5" />
          </span>
          <span className="text-[16px] sm:text-[17px] font-semibold text-[#111827] leading-[1.4] pt-1.5" style={{ letterSpacing: "-0.01em" }}>
            {entry.question}
          </span>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-[#9ca3af] shrink-0 mt-2 transition-transform duration-200 ${isOpen ? "rotate-180 text-[#1a56db]" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="px-6 sm:px-7 pb-7 -mt-1 pl-[76px]">
          <p className="text-[14.5px] sm:text-[15px] text-[#4b5563] leading-[1.75]">{entry.answer}</p>
          <Link
            to={`/faq/${entry.slug}`}
            onClick={() => setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 50)}
            className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#1a56db] hover:underline"
            data-testid={`faq-readmore-${idx}`}
          >
            Read full answer <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
};

export const FAQContent = () => {
  const navigate = useNavigate();
  const items = FAQ_ENTRIES;
  const [openIdx, setOpenIdx] = useState(0);

  const half = Math.ceil(items.length / 2);
  const colA = items.slice(0, half);
  const colB = items.slice(half);

  return (
    <PageShell>
      <StructuredData />

      {/* ── HERO ── */}
      <section className="bg-white py-16 lg:py-20" data-testid="faq-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-16 items-start">
            <div>
              <div
                className="inline-flex items-center gap-2 rounded-full bg-[#e8eefb] px-4 py-1.5 text-[13px] font-semibold text-[#1a56db] mb-7"
                data-testid="faq-pill"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                AI Strategy FAQ for Executives
              </div>
              <h1
                className="text-[40px] sm:text-[48px] lg:text-[56px] font-bold text-[#111827] leading-[1.05] tracking-tight"
                style={{ letterSpacing: "-0.5px" }}
                data-testid="faq-headline"
              >
                The 15 questions CEOs are actually asking AI.
              </h1>
              <p className="mt-6 text-[16.5px] sm:text-[17.5px] text-[#374151] leading-[1.7] max-w-[680px]">
                Direct, executive-friendly answers to the questions that come up most
                often when leadership teams evaluate AI for their business. Each answer
                links to a focused deep-dive page with practical guidance.
              </p>
            </div>

            {/* Quick info card */}
            <aside
              className="bg-white border border-[#e5e7eb] rounded-2xl p-7 shadow-[0_4px_24px_rgba(17,24,39,0.06)]"
              data-testid="faq-quick-card"
            >
              <div className="text-[11px] font-bold tracking-[0.14em] text-[#9ca3af] uppercase mb-5">
                Still need help?
              </div>
              <p className="text-[14.5px] text-[#4b5563] leading-[1.7]">
                Most teams move forward after a 30-minute working session. We come prepared,
                no deck, no sales pitch.
              </p>
              <Button
                onClick={() => navigate("/contact?intent=book")}
                className="mt-6 h-12 rounded-xl bg-[#1a56db] px-5 text-white text-[14.5px] font-semibold hover:bg-[#143faa] w-full shadow-none"
                data-testid="faq-book-cta"
              >
                Book a Conversation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a
                href="mailto:ddacosta33@yahoo.com"
                className="mt-3 inline-flex items-center gap-2 text-[13.5px] text-[#1a56db] font-semibold hover:underline"
                data-testid="faq-email-link"
              >
                <Mail className="h-4 w-4" />
                ddacosta33@yahoo.com
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* ── FAQ LIST ── */}
      <section className="bg-[#f9fafb] border-t border-[#e5e7eb] py-20 lg:py-24" data-testid="faq-list-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-3">
                The shortlist
              </div>
              <h2
                className="text-[26px] lg:text-[34px] font-bold text-[#111827] leading-[1.2] tracking-tight"
                style={{ letterSpacing: "-0.5px" }}
              >
                Practical answers, executive language.
              </h2>
            </div>
            <button
              onClick={() => setOpenIdx(openIdx === -1 ? 0 : -1)}
              className="text-[13.5px] font-semibold text-[#1a56db] hover:underline"
              data-testid="faq-toggle-all"
            >
              {openIdx === -1 ? "Expand first" : "Collapse all"}
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-5" data-testid="faq-grid">
            <div className="space-y-5">
              {colA.map((entry, i) => (
                <FAQItem
                  key={entry.slug}
                  entry={entry}
                  idx={i}
                  isOpen={openIdx === i}
                  onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                />
              ))}
            </div>
            <div className="space-y-5">
              {colB.map((entry, i) => {
                const realIdx = i + half;
                return (
                  <FAQItem
                    key={entry.slug}
                    entry={entry}
                    idx={realIdx}
                    isOpen={openIdx === realIdx}
                    onToggle={() => setOpenIdx(openIdx === realIdx ? -1 : realIdx)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-[#0f172a] py-20 lg:py-24" data-testid="faq-final-cta">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 text-center">
          <Sparkles className="mx-auto h-7 w-7 text-[#E8A020] mb-5" />
          <h2
            className="text-[30px] lg:text-[40px] font-bold text-white leading-[1.15] tracking-tight max-w-3xl mx-auto"
            style={{ letterSpacing: "-0.5px" }}
          >
            Question we didn't cover?
          </h2>
          <p className="mt-5 text-[15.5px] text-[#cbd5e1] leading-[1.7] max-w-2xl mx-auto">
            Bring it to a 30-minute working session. We'll answer it directly, with
            specifics drawn from your operating model.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => navigate("/contact?intent=book")}
              className="h-14 rounded-xl bg-[#1a56db] px-7 text-white text-[15px] font-semibold hover:bg-[#143faa] shadow-none"
              data-testid="faq-final-cta-book"
            >
              Book a Conversation
              <ArrowRight className="ml-2 h-[18px] w-[18px]" />
            </Button>
            <a
              href="mailto:ddacosta33@yahoo.com"
              className="h-14 inline-flex items-center gap-2 rounded-xl border border-white/20 bg-transparent text-white text-[15px] font-semibold hover:bg-white/10 px-6"
              data-testid="faq-final-cta-email"
            >
              <Mail className="h-[18px] w-[18px]" /> Email Dirk
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default FAQContent;
